import { AnalyticsEvents, trackEvent } from 'app/shared/u21-ui/analytics';
import { ComponentProps, FocusEventHandler, HTMLProps, ReactNode } from 'react';

import { startOfDay } from 'date-fns';
import { getDOMProps } from 'app/shared/utils/react';

import { BackdropProps, TextFieldProps } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  IconChevronLeft,
  IconChevronDown,
  IconChevronRight,
  IconCalendar,
} from '@u21/tabler-icons';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { DateInputField } from 'app/shared/u21-ui/components/input/date-picker/DateInputField';
import { PickersDay } from 'app/shared/u21-ui/components/input/date-picker/PickersDay';

export interface U21DatePickerProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'onBlur' | 'onChange' | 'onFocus' | 'value'
> {
  autoFocus?: boolean;
  backdrop?: boolean;
  backdropProps?: Omit<BackdropProps, 'open'>;
  clearable?: boolean;
  disabled?: boolean;
  endIcon?: ReactNode;
  error?: boolean;
  id?: string;
  inputProps?: TextFieldProps['inputProps'];
  label?: string;
  maxDate?: Date;
  minDate?: Date;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange: (date?: Date) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  openTo?: 'year' | 'month' | 'day';
  placeholder?: string;
  required?: boolean;
  value?: Date;
  valueTransform?: (value: Date) => Date;
}

function defaultValueTransform(value: Date): Date {
  return startOfDay(value);
}

const DATE_FORMAT = 'PP';

// defined based on MUI's default min / max
// github.com/mui/mui-x/blob/f074f2735552161fd86d07cca5154e2bcabb263f/packages/x-date-pickers/src/LocalizationProvider/LocalizationProvider.tsx#L126-L127
const DEFAULT_MIN_DATE = new Date('1900-01-01T00:00:00.000Z');
const DEFAULT_MAX_DATE = new Date('2099-12-31T00:00:00.000Z');

export const U21DatePicker = (props: U21DatePickerProps) => {
  const {
    autoFocus,
    backdrop,
    backdropProps,
    clearable = true,
    disabled,
    error,
    id,
    inputProps = {},
    label,
    maxDate = DEFAULT_MAX_DATE,
    minDate = DEFAULT_MIN_DATE,
    name,
    onChange,
    placeholder = disabled ? undefined : 'Select a date',
    required,
    value,
    valueTransform = defaultValueTransform,
    endIcon,
    onBlur,
    onFocus,
    openTo,
    ...rest
  } = props;

  const onChangeWrapper = (rawValue: Date | null | undefined) => {
    const newValue = rawValue ? valueTransform(rawValue) : rawValue;
    trackEvent(
      AnalyticsEvents.U21DATEPICKER_ON_CHANGE,
      props,
      {},
      newValue ?? undefined,
    );
    onChange(newValue ?? undefined);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        slots={{
          leftArrowIcon: IconChevronLeft,
          rightArrowIcon: IconChevronRight,
          switchViewIcon: IconChevronDown,
          field: DateInputField,
          day: PickersDay,
        }}
        disabled={disabled}
        format={DATE_FORMAT}
        maxDate={maxDate}
        minDate={minDate}
        onChange={onChangeWrapper}
        openTo={openTo}
        slotProps={{
          desktopPaper: {
            // prevent input from losing focus when clicking on popup
            onMouseDown: (e) => e.preventDefault(),
          },
          popper: {
            placement: 'bottom',
            // must be greater than backdrop
            // number chosen based on https://mui.com/material-ui/customization/z-index
            ...(backdrop ? { style: { zIndex: 1400 } } : {}),
          },
          field: {
            autoFocus,
            backdrop,
            backdropProps,
            clearable,
            dateFormat: DATE_FORMAT,
            disabled,
            endIcon,
            error,
            id,
            inputProps,
            label,
            maxDate,
            minDate,
            name,
            onBlur,
            onChange: onChangeWrapper,
            onFocus,
            placeholder,
            required,
            startIcon: <IconCalendar />,
            value,
          } satisfies ComponentProps<typeof DateInputField> as any,
        }}
        value={value ?? null}
        views={['year', 'month', 'day']}
        {...getDOMProps(rest)}
      />
    </LocalizationProvider>
  );
};
